import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "other", "submit"]
  static values = {
    variant: String,
    markSeenUrl: String,
    otherValues: { type: Array, default: [] }
  }

  connect() {
    this.toggleOtherField()
    this.validate()
    this.bindIntroDismissOnClose()
  }

  disconnect() {
    if (!this.boundDismissOnClosed) return

    this.element.removeEventListener("railsui-modal:closed", this.boundDismissOnClosed)
    this.boundDismissOnClosed = null
  }

  toggleOtherField() {
    if (!this.hasOtherTarget) return

    const needsOther = this.otherValuesValue.includes(this.courseValue())
    this.otherTarget.classList.toggle("hidden", !needsOther)
    if (needsOther) {
      this.otherTarget.setAttribute("required", "required")
    } else {
      this.otherTarget.removeAttribute("required")
      this.otherTarget.value = ""
    }
  }

  validate() {
    this.toggleOtherField()
    if (!this.hasSubmitTarget) return
    if (this.submitting) return

    this.submitTarget.disabled = !this.formIsValid()
  }

  validateSubmit(event) {
    if (this.formIsValid()) {
      if (this.submitting) {
        event.preventDefault()
        return
      }

      this.submitting = true
      if (this.hasSubmitTarget) this.submitTarget.disabled = true
      return
    }

    event.preventDefault()
    this.validate()
  }

  dismissIntro() {
    if (this.variantValue !== "intro") return
    if (this.introMarkedSeen) return
    if (!this.markSeenUrlValue) return

    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (!token) return

    this.introMarkedSeen = true

    fetch(this.markSeenUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": token,
        Accept: "application/json"
      },
      credentials: "same-origin",
      keepalive: true
    })
      .then((response) => {
        if (!response.ok) this.introMarkedSeen = false
      })
      .catch(() => {
        this.introMarkedSeen = false
      })
  }

  // Contract: railsui-modal dispatches "railsui-modal:closed" from close() so
  // intro dismiss / mark-seen runs for X, Cancel, Esc, and overlay close paths.
  bindIntroDismissOnClose() {
    if (this.variantValue !== "intro") return
    if (this.boundDismissOnClosed) return

    this.boundDismissOnClosed = () => this.dismissIntro()
    this.element.addEventListener("railsui-modal:closed", this.boundDismissOnClosed)
  }

  formIsValid() {
    const course = this.courseValue()
    if (!course) return false

    if (this.otherValuesValue.includes(course) && !this.otherTarget?.value?.trim()) {
      return false
    }

    const startDate = this.dateInput("start_date")?.value
    const endDate = this.dateInput("end_date")?.value
    if (!startDate || !endDate) return false

    const start = this.parseDate(startDate)
    const end = this.parseDate(endDate)
    if (!start || !end) return false
    if (end <= start) return false

    if (this.variantValue === "expired" && end < this.startOfToday()) {
      return false
    }

    return true
  }

  courseValue() {
    if (!this.hasFormTarget) return ""

    return this.formTarget.querySelector("[name='clerkship_rotation_course']")?.value || ""
  }

  dateInput(name) {
    if (!this.hasFormTarget) return null

    return this.formTarget.querySelector(`[name='${name}']`)
  }

  parseDate(value) {
    const match = String(value || "").trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
    if (!match) return null

    const month = Number(match[1])
    const day = Number(match[2])
    const year = Number(match[3])
    const date = new Date(year, month - 1, day)

    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null
    }

    return date
  }

  startOfToday() {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }
}
